<script>
// Topbar clara da navegação 2026. Esquerda: o CONTEXTO (cliente selecionado na
// visão de cliente; "Gestão do escritório" na gestão) — o módulo/página NÃO é
// repetido aqui, pois já aparece na sidebar e na sub-nav da página.
// Centro: busca opcional. Direita: data + notificações + avatar do usuário.
import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import { deveVerSoFeeFixo } from '@utils/escritorioFeeFixoMigracao'
// Mesmo constante que o `atalho-ia-imersiva` importa — o nome da rota mora num
// lugar só, e não como string solta aqui.
import { ROTA_IMERSIVA } from '@modules/aawz-intelligence-v2/views/chat-imersivo/constants'
import TeleportTarget from '@components/teleport/teleport-target.vue'
import TopbarNotifications from './topbar-notifications.vue'
import TopbarUserMenu from './topbar-user-menu.vue'

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]

export default {
  name: 'NavTopbar',
  components: { TopbarNotifications, TopbarUserMenu, TeleportTarget },
  props: {
    variant: {
      type: String,
      default: 'gestao',
      validator: (v) => ['gestao', 'cliente'].includes(v),
    },
    client: { type: Object, default: () => ({}) },
    showHamburger: { type: Boolean, default: false },
  },
  computed: {
    isCliente() {
      return this.variant === 'cliente'
    },
    // Rótulo do atalho: ⌘K no Mac, Ctrl K no restante (padrão do createContextMenu).
    searchShortcutLabel() {
      return navigator.userAgent.includes('Mac') ? '⌘K' : 'Ctrl K'
    },
    clientName() {
      return this.client && this.client.name ? this.client.name : ''
    },
    clientInitials() {
      return this.clientName
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase()
    },
    today() {
      const d = new Date()
      return `${String(d.getDate()).padStart(2, '0')} de ${
        MESES[d.getMonth()]
      }, ${d.getFullYear()}`
    },
    // Escritório Fee Fixo Migração, não-gestor, e cliente final (IS_CLIENTE):
    // sem busca global — a funcionalidade já sai gateada no próprio
    // global-search.vue, isto só some com o gatilho pra não deixar um botão
    // morto (achado do usuário, 12/08/2026 e 18/08/2026).
    showSearch() {
      const user = this.$currentUser()
      return !deveVerSoFeeFixo(user) && !user.IS_CLIENTE
    },
  },
  methods: {
    /**
     * Na `/ia` a lupa abre o palette da IA — o MESMO que o Ctrl+K faz ali.
     *
     * Sem isto, os dois gatilhos da mesma caixa divergiam: o atalho caía no
     * palette (conversas, agentes, modelos) e o clique caía na busca global,
     * que dentro do chat não sabe responder por nada disso.
     *
     * ⚠️ A decisão é por ROTA, e não emitindo sempre para o palette e deixando
     * ele redistribuir. O `open()` dele já devolve para a busca global fora da
     * `/ia` — e essa continua sendo a rede de segurança —, mas fazer a lupa de
     * TODA a plataforma endereçar o modal da IA deixaria a busca inteira
     * dependendo dele estar montado.
     */
    openSearch() {
      const naIa = this.$route && this.$route.name === ROTA_IMERSIVA
      GlobalBus.$emit(naIa ? MODAL.IA_PALETTE : MODAL.GLOBAL_SEARCH)
    },
  },
}
</script>

<template>
  <div class="nav-topbar">
    <button
      v-if="showHamburger"
      class="nav-topbar__hamburger"
      type="button"
      title="Menu"
      @click="$emit('toggle-menu')"
    >
      <icon-svg name="mdi-menu" :size="20" color="#030309" />
    </button>

    <!-- Contexto: só o cliente selecionado (visão cliente). Na gestão não há
         crumb — o escritório já aparece no brand da sidebar. -->
    <nav class="nav-topbar__crumb">
      <template v-if="isCliente && clientName">
        <span class="nav-topbar__client-avatar">{{ clientInitials }}</span>
        <span class="nav-topbar__client-name">{{ clientName }}</span>
      </template>
      <!-- Destino para uma tela publicar o próprio contexto AQUI, ao lado do
           crumb — hoje a visão imersiva do chat de IA põe o "Voltar para
           Gestão" e a identidade do agente. Fica vazio quando ninguém publica,
           e o `__spacer` a seguir absorve a diferença. Mesmo mecanismo do
           `page-header-actions` da nav-bar de módulo. -->
      <TeleportTarget name="topbar-inicio" class="nav-topbar__inicio" />
    </nav>

    <div class="nav-topbar__spacer" />

    <!-- Gatilho da busca global (command palette): abre por clique ou Ctrl/⌘K.
         O input real fica dentro do palette (MODAL.GLOBAL_SEARCH).
         Design minimalista/discreto (BaseButton fit-content) — o placeholder
         herda font-weight:600 do BaseButton, mas o span abaixo força 400. -->
    <BaseButton
      v-if="showSearch"
      class="nav-topbar__search"
      fit-content
      :aria-label="`Buscar páginas, ações ou clientes (${searchShortcutLabel})`"
      @click.native="openSearch"
    >
      <icon-svg name="search" :size="16" color="secondary" />
      <span class="nav-topbar__search-placeholder">Buscar</span>
      <span class="nav-topbar__search-kbd">{{ searchShortcutLabel }}</span>
    </BaseButton>

    <div class="nav-topbar__actions">
      <span class="nav-topbar__date">{{ today }}</span>
      <TopbarNotifications />
      <TopbarUserMenu />
    </div>
  </div>
</template>

<style lang="sass" scoped>
.nav-topbar
  flex-shrink: 0
  display: flex
  align-items: center
  gap: 16px
  height: 44px
  padding: 0 24px
  background: #fff
  border-bottom: 1px solid #ebebeb

  &__hamburger
    flex-shrink: 0
    width: 36px
    height: 36px
    border-radius: 10px
    border: 1px solid #e4e4e4
    background: #fff
    cursor: pointer
    display: inline-flex
    align-items: center
    justify-content: center

  &__crumb
    display: flex
    align-items: center
    gap: 8px
    min-width: 0
    // ★ Estica na vertical para que uma tela publicante possa ancorar conteúdo
    // na BORDA de baixo da barra — hoje o sublinhado da aba ativa da visão
    // imersiva do chat, que precisa encostar no hairline inferior. Sem conteúdo
    // teleportado nada muda de lugar: o crumb continua `align-items: center`,
    // então avatar e nome do cliente seguem centrados na mesma altura de antes.
    align-self: stretch

  &__client-avatar
    width: 26px
    height: 26px
    border-radius: 8px
    background: var(--primary)
    color: #fff
    font-size: 10.5px
    font-weight: 700
    display: inline-flex
    align-items: center
    justify-content: center
    flex-shrink: 0

  &__client-name
    font-size: 14px
    font-weight: 700
    color: #030309
    white-space: nowrap

  &__context
    font-size: 13px
    font-weight: 700
    letter-spacing: 0.04em
    color: #8e8e8e
    white-space: nowrap

  // Alvo do teleport: sem conteúdo publicado ele não reserva espaço — largura
  // zero, nada dentro. O `align-self: stretch` propaga a altura do crumb (ver
  // acima) para quem publica; num alvo vazio ele estica uma caixa de 0px de
  // largura, que continua sendo nada na tela.
  &__inicio
    display: flex
    align-items: center
    gap: 12px
    min-width: 0
    align-self: stretch

  &__spacer
    flex: 1
    min-width: 12px

  &__search
    display: flex
    align-items: center
    gap: 8px
    width: 280px
    height: 38px
    padding: 0 14px
    border-radius: 999px
    border: 0
    background: var(--neutral-100)
    cursor: pointer
    transition: background 0.2s ease

    &:hover
      background: var(--neutral-200)

    &:focus-visible
      outline: 2px solid var(--primary)
      outline-offset: 2px

  &__search-placeholder
    flex: 1
    min-width: 0
    text-align: left
    font-size: 13px
    font-weight: 400
    color: var(--secondary)
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

  &__search-kbd
    flex-shrink: 0
    font-size: 11px
    color: var(--secondary)
    border: 1px solid var(--neutral-200)
    border-radius: 4px
    padding: 1px 6px
    line-height: 1.4

  &__actions
    display: flex
    align-items: center
    gap: 14px

  &__date
    font-size: 13px
    color: #5b5b5b
    font-weight: 600
    white-space: nowrap

</style>
